import { apiFetch, ApiNetworkError } from './apiClient';

export interface ApiProblem {
  readonly title?: unknown;
  readonly detail?: unknown;
}

function capitalize(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** The shared "no JSON body, or no usable detail/title in it" fallback every resource module
 *  used before this extraction. Exported so a module with its own richer error parsing (e.g.
 *  moleculeSearch.ts's validation-violation messages) can still share this base case. */
export async function defaultErrorMessage(response: Response, resourceLabel: string): Promise<string> {
  const contentType = response.headers.get('content-type') ?? '';
  const fallback = `${capitalize(resourceLabel)} request failed with status ${response.status}.`;
  if (!contentType.includes('json')) return fallback;
  const body = (await response.json().catch(() => undefined)) as ApiProblem | undefined;
  const detail = typeof body?.detail === 'string' ? body.detail : undefined;
  const title = typeof body?.title === 'string' ? body.title : undefined;
  return detail ?? title ?? fallback;
}

/**
 * The request/error-handling path every api-client resource module (except moleculeDrawings.ts,
 * see below) used to duplicate per exported function: apiFetch's network-error case, the fixed
 * 401 "session expired" message, and JSON problem-detail parsing on a non-ok response.
 * `resourceLabel` (e.g. "molecule", "bond taxonomy") feeds both the network-error message and,
 * by default, `defaultErrorMessage`'s fallback text. Pass `parseErrorMessage` to override the
 * non-ok-response message for a module with its own richer error shape.
 *
 * Not used by moleculeDrawings.ts: it needs `status`/`currentRevision` preserved via its own
 * `MoleculeDrawingApiError` for structured 409-conflict handling, which a plain message string
 * can't carry -- forcing it onto this shared, message-only path would be a real behavior loss,
 * not deduplication.
 */
export async function apiRequest<T>(
  path: string,
  resourceLabel: string,
  init?: RequestInit,
  options?: { readonly parseErrorMessage?: (response: Response) => Promise<string> }
): Promise<T> {
  let response: Response;
  try {
    response = await apiFetch(path, init);
  } catch (error) {
    throw new Error(
      error instanceof ApiNetworkError
        ? `Unable to reach the ${resourceLabel} API: ${error.message}`
        : `Unable to reach the ${resourceLabel} API.`
    );
  }
  if (response.status === 401) {
    throw new Error('Session expired. Redirecting to sign in…');
  }
  if (!response.ok) {
    const parseErrorMessage = options?.parseErrorMessage ?? ((r: Response) => defaultErrorMessage(r, resourceLabel));
    throw new Error(await parseErrorMessage(response));
  }
  // No content (a 204 from an action like publishing): nothing to parse.
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
